import { PublicBulletinResource, PublicContactItem } from "@rubi/api-generated";

import {
  IBulletinFlag,
  IBulletinResolution,
  ICreator,
  IEvidenceFile,
  ITag,
} from "@/types";

export type PublicAlertAddress = {
  description?: string;
  place_id?: string;
  location_classification?: string;
  other_location_classification?: string;
  address_components?: google.maps.GeocoderAddressComponent[];
  geometry?: google.maps.LatLngLiteral;
  location_type?: string;
  types?: string[];
  plus_code?: google.maps.GeocoderResult["plus_code"];
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
  precise?: boolean;
};

export type PublicAlertLocation = {
  addresses?: PublicAlertAddress[];
};

export type PublicAlertContact = PublicContactItem & {
  phone?: string | null;
  extension?: string | null;
};

export type PublicAlertDetailResource = Omit<
  PublicBulletinResource,
  "evidence_files" | "location"
> & {
  approvedBy?: string | null;
  approvedByBadge?: string | null;
  operationalArea?: string | null;
  bulletinId?: number;
  tagDict?: Record<string, ITag>;
  tags?: Record<string, ITag>;
  creator?: ICreator | null;
  resolutions?: IBulletinResolution[];
  contacts?: PublicAlertContact[];
  public_contact?: PublicAlertContact[];
  description?: string | null;
  descriptionFr?: string | null;
  evidence_files?: Record<string, IEvidenceFile>;
  flags?: IBulletinFlag[];
  location?: PublicAlertLocation;
  originalLanguage?: string | null;
  isDraft?: boolean;
  classification?: string | null;
  issuing_police_service_name?: string;
  occurrence_number?: string[];
};

export type PublicPoliceServicesGroupResponse = {
  map_center?: google.maps.LatLngLiteral | null;
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object";

export const isPublicAlertAddress = (
  value: unknown,
): value is PublicAlertAddress => {
  if (!isRecord(value)) return false;
  const geometry = value.geometry;
  return (
    geometry === undefined ||
    (isRecord(geometry) &&
      typeof geometry.lat === "number" &&
      typeof geometry.lng === "number")
  );
};

export const getPublicAlertAddresses = (
  location: PublicBulletinResource["location"],
): PublicAlertAddress[] => {
  if (!isRecord(location) || !Array.isArray(location.addresses)) return [];
  return location.addresses.filter(isPublicAlertAddress);
};

export const isEvidenceFile = (value: unknown): value is IEvidenceFile => {
  if (!isRecord(value)) return false;
  return (
    typeof value.content_type === "string" &&
    typeof value.download_url === "string" &&
    typeof value.filename === "string" &&
    typeof value.thumbnail_url === "string"
  );
};

export const getPublicAlertEvidenceFiles = (
  evidenceFiles: PublicBulletinResource["evidence_files"],
): IEvidenceFile[] => {
  if (!isRecord(evidenceFiles)) return [];
  return Object.values(evidenceFiles).filter(isEvidenceFile);
};
